/**
 * 费用计算配置（图片算力 / 市场工具 / 模型）—
 * 支持成本配置保存、单次成本计算、用户/系统消耗人民币估算
 */
import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { showToast } from './utils.js';
import { formatBeijingDateKey, formatBeijingMonthStart } from '../time-beijing.js';
import {
  renderCostStatusState,
  renderTopUsersView,
} from './presentation/cost-calculation-usage.js';
import {
  renderCostFormulaStrip,
  renderCostOverviewView,
} from './presentation/cost-calculation-summary.js';
import { renderCostCalculationConfiguration } from './presentation/cost-calculation-configuration.js';
import { createCostCalculationConfigurationController } from './cost-calculation-configuration-controller.js';
import { createUsageLimitsController } from './cost-calculation-usage-limits-controller.js';
import { createAdminCostCalculationFacade } from '../interaction/facades/admin-cost-calculation.facade.js';
import {
  DEFAULT_MODEL_RATE,
  calculateModelCost,
  getDefaultModelCandidate,
  getModelOptionValue,
  getUsageMarketCalls,
  guessModelProvider,
  parseModelOptionValue,
} from './cost-calculation-model-contract.js';

let runtimeSettings = [];
let usagePayload = null;
const facade = createAdminCostCalculationFacade({ request: authFetch });
const configuration = createCostCalculationConfigurationController({
  getRuntimeSettings: () => runtimeSettings,
  setRuntimeSettings: (settings) => { runtimeSettings = settings; },
  reload: loadCostCalculationSettings,
  saveRuntimeSettings: facade.saveRuntimeSettings,
});
const usageLimits = createUsageLimitsController({
  facade,
  formatCurrency,
  formatCount,
  getRoot: () => document.getElementById('cost-calc-content'),
});

export function initCostCalculationSettings() {
  registerViewLoader('admin-cost-calculation', () => loadCostCalculationSettings());
  document.getElementById('cost-calc-refresh-btn')?.addEventListener('click', () => {
    loadCostCalculationSettings();
  });
  document.getElementById('cost-calc-save-btn')?.addEventListener('click', () => {
    configuration.save();
  });
}

function getSelectedModelFromDom(modelCandidates = []) {
  const select = document.getElementById('cost-model-target');
  if (!select) {
    const fallback = getDefaultModelCandidate(modelCandidates);
    return {
      name: fallback.name || '',
      provider: fallback.provider || 'minimax',
      label: fallback.label || fallback.name || '',
      value: getModelOptionValue(fallback),
    };
  }
  const parsed = parseModelOptionValue(select.value);
  const matched = modelCandidates.find((item) => item.name === parsed.name && item.provider === parsed.provider) || parsed;
  return {
    name: matched?.name || parsed.name || '',
    provider: matched?.provider || parsed.provider || 'minimax',
    label: matched?.label || matched?.name || parsed.name || '',
    value: getModelOptionValue({
      provider: matched?.provider || parsed.provider || 'minimax',
      name: matched?.name || parsed.name || '',
    }),
  };
}

async function loadCostCalculationSettings() {
  const statusEl = document.getElementById('cost-calc-status');
  const content = document.getElementById('cost-calc-content');
  const overview = document.getElementById('cost-calc-overview');
  if (!content || !overview) return;

  setLoadingMessage(content, overview, '正在加载配置…');

  try {
    const pageData = await facade.loadPageData();
    runtimeSettings = pageData.runtimeSettings;
    usagePayload = pageData.usage;
    usageLimits.replacePayload(pageData.usageLimits);
    usageLimits.clearSelection();
    renderCostCalculationView();
    if (statusEl) {
      statusEl.textContent = '配置已加载';
    }
  } catch (err) {
    console.error('Load cost calculation settings failed', err);
    if (statusEl) statusEl.textContent = '加载失败';
    showToast(err.message || '加载配置失败', 'error');
    setErrorMessage(content, overview, err.message || '加载失败');
  }
}

function setLoadingMessage(content, overview, message) {
  renderCostStatusState({ content, overview, message, tone: 'loading' });
}

function setErrorMessage(content, overview, message) {
  renderCostStatusState({ content, overview, message, tone: 'error' });
}

function renderCostCalculationView() {
  const content = document.getElementById('cost-calc-content');
  const overview = document.getElementById('cost-calc-overview');
  if (!content || !overview) return;

  const {
    settingMap,
    image,
    marketTools,
    modelRates,
    modelCandidates,
  } = configuration.getPersistedConfiguration();
  const defaultModelOption = getModelOptionValue(getDefaultModelCandidate(modelCandidates));

  renderOverview(overview, image, marketTools, modelRates, modelCandidates, settingMap);
  renderUsageRangeHint();

  renderCostCalculationConfiguration(content, {
    image,
    marketTools,
    modelRates,
    modelCandidates: modelCandidates.map((item) => ({
      ...item,
      value: getModelOptionValue(item),
    })),
    defaultModelOption,
    formatCurrency,
  });

  bindCostCalcEvents();
  updateCostFormulaStrips(image, marketTools);
  usageLimits.renderTable(content);
  renderTopUsersTable();
}

// ── Top 5 消耗用户表格（新增功能，不删减现有限额表格） ──
function renderTopUsersTable() {
  const container = document.getElementById('cost-calc-top-users');
  if (!container) return;

  const users = Array.isArray(usagePayload?.users) ? usagePayload.users : [];
  // 排序优先级：1) costs.totalCny 降序；2) token 总量 + 调用次数 fallback（费用未配置时也能展示活跃用户）
  const topUsers = users
    .filter((u) => u && (
      Number(u.costs?.totalCny || 0) > 0
      || Number(u.textTotalTokens || 0) > 0
      || Number(u.sellerspriteCalls || 0) > 0
      || Number(u.imagesGenerated || 0) > 0
      || Number(u.linkfoxCredits || 0) > 0
    ))
    .sort((a, b) => {
      const costA = Number(a.costs?.totalCny || 0);
      const costB = Number(b.costs?.totalCny || 0);
      if (costA !== costB) return costB - costA;
      // 费用相同（含都为 0）时按活动量降序
      const activityA = Number(a.textTotalTokens || 0) + Number(a.sellerspriteCalls || 0) + Number(a.imagesGenerated || 0);
      const activityB = Number(b.textTotalTokens || 0) + Number(b.sellerspriteCalls || 0) + Number(b.imagesGenerated || 0);
      return activityB - activityA;
    })
    .slice(0, 5);

  if (topUsers.length === 0) {
    renderTopUsersView(container, { topUsers, totals: null, formatCurrency });
    return;
  }

  const totals = topUsers.reduce((acc, u) => {
    acc.image += Number(u.costs.linkfoxCny || 0);
    acc.market += Number(u.costs.marketDataCny || 0);
    acc.model += Number(u.costs.textModelCny || 0);
    acc.total += Number(u.costs.totalCny || 0);
    return acc;
  }, { image: 0, market: 0, model: 0, total: 0 });

  renderTopUsersView(container, { topUsers, totals, formatCurrency });
}

function bindCostCalcEvents() {
  const root = document.getElementById('cost-calc-content');
  if (!root) return;
  const update = () => {
    const settingMap = configuration.getSettingMap();
    const {
      image,
      marketTools,
      modelRates,
      modelCandidates,
    } = configuration.getDomConfiguration();
    renderOverview(document.getElementById('cost-calc-overview'), image, marketTools, modelRates, modelCandidates, settingMap);
    updateCostFormulaStrips(image, marketTools);
    usageLimits.renderTable(root);
  };

  root.querySelectorAll('[data-cost-tab]').forEach((tab) => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.costTab;
      root.querySelectorAll('[data-cost-tab]').forEach((item) => {
        const active = item.dataset.costTab === target;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      root.querySelectorAll('[data-cost-panel]').forEach((panel) => {
        const active = panel.dataset.costPanel === target;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    });
  });

  root.querySelectorAll('.cost-input, .cost-select, .cost-model-input, .cost-model-output').forEach((el) => {
    el.addEventListener('input', update);
    el.addEventListener('change', update);
  });

  usageLimits.bindEvents(root);
}

function renderOverview(overview, image, marketTools, modelRates, modelCandidates, settingMap) {
  if (!overview) return;
  const snapshot = buildCostSnapshot(image, marketTools, modelRates, modelCandidates, settingMap);
  renderCostOverviewView(overview, {
    snapshot,
    formatCurrency,
    formatCount,
    formatPercent,
    formatProgressValue,
  });
}

function updateCostFormulaStrips(image, marketTools) {
  const imageUnitCost = image.totalUnits > 0 ? image.totalFee / image.totalUnits : 0;
  renderCostFormulaStrip(document.getElementById('cost-image-formula'), {
    totalFee: image.totalFee,
    denominator: image.totalUnits,
    unitCost: imageUnitCost,
    formatCurrency,
    formatCount,
  });
  ['sellersprite', 'sorftime'].forEach((key) => {
    const tool = marketTools[key];
    const denom = Number(tool.totalCalls || 0) + Number(tool.openingBalance || 0);
    const unitCost = denom > 0 ? Number(tool.totalFee || 0) / denom : 0;
    renderCostFormulaStrip(document.getElementById(`cost-${key}-formula`), {
      totalFee: tool.totalFee,
      denominator: denom,
      unitCost,
      formatCurrency,
      formatCount,
    });
  });
}

function buildCostSnapshot(image, marketTools, modelRates, modelCandidates, settingMap) {
  const usage = usagePayload?.systemTotal || {};
  const imageUnits = Number(image.totalUnits || 0);
  const imageFee = Number(image.totalFee || 0);
  const imageUnitCost = imageUnits > 0 ? imageFee / imageUnits : 0;
  const imageTotalImagePower = Number(usage.totalImagesGenerated || 0);

  const activeMarketProvider = String(settingMap?.get('marketData.provider')?.value || 'sellersprite').toLowerCase();
  const activeMarket = (activeMarketProvider.includes('sellersprite') || activeMarketProvider === 'sellersprite')
    ? marketTools.sellersprite
    : marketTools.sorftime;
  const marketCallsDenom = Number(activeMarket.totalCalls || 0) + Number(activeMarket.openingBalance || 0);
  const marketUnitCost = marketCallsDenom > 0 ? Number(activeMarket.totalFee || 0) / marketCallsDenom : 0;

  const selectedModelObj = getSelectedModelFromDom(modelCandidates);
  const selectedModel = selectedModelObj.name;
  const selectedProvider = selectedModelObj.provider || guessModelProvider(selectedModel, modelCandidates);
  const selectedModelConfig = selectedModel ? (modelRates[selectedModel] || DEFAULT_MODEL_RATE) : DEFAULT_MODEL_RATE;
  const normalizedProvider = String(selectedProvider || '').toLowerCase();
  const modelInputTokens = normalizedProvider === 'gemini'
    ? Number(usage.totalGeminiInputTokens || 0)
    : Number(usage.totalMinimaxInputTokens ?? usage.totalTextInputTokens ?? 0);
  const modelOutputTokens = normalizedProvider === 'gemini'
    ? Number(usage.totalGeminiOutputTokens || 0)
    : Number(usage.totalMinimaxOutputTokens ?? usage.totalTextOutputTokens ?? 0);
  const modelEstimate = calculateModelCost(modelInputTokens, modelOutputTokens, selectedModelConfig);

  const imageEstimate = imageUnitCost * imageTotalImagePower;
  const marketCalls = getUsageMarketCalls(usage);
  const marketEstimate = marketUnitCost * marketCalls;
  const totalEstimate = imageEstimate + marketEstimate + modelEstimate;

  return {
    imageFee,
    imageUnits,
    imageUnitCost,
    imageEstimate,
    imageUsagePercent: imageFee > 0 ? (imageEstimate / imageFee) * 100 : 0,
    activeMarket,
    activeMarketProvider,
    marketShortLabel: marketShortLabel(activeMarketProvider),
    marketCycleLabel: activeMarket.billingCycle === 'year' ? '年费' : '月费',
    marketUnitCost,
    marketEstimate,
    marketUsagePercent: Number(activeMarket.totalFee || 0) > 0 ? (marketEstimate / Number(activeMarket.totalFee || 0)) * 100 : 0,
    selectedModel,
    selectedProvider,
    selectedModelLabel: selectedModelObj.label || selectedModel || 'MiniMax M3',
    selectedModelConfig,
    modelEstimate,
    modelUsagePercent: totalEstimate > 0 ? (modelEstimate / totalEstimate) * 100 : 0,
    totalEstimate,
  };
}

function formatProgressValue(value) {
  const num = Math.max(0, Math.min(100, Number(value || 0)));
  return Number.isFinite(num) ? num.toFixed(2) : '0';
}

function formatPercent(value) {
  return `${Math.round(Number(formatProgressValue(value)))}%`;
}

function formatCount(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
}

function marketShortLabel(provider) {
  if (String(provider || '').toLowerCase() === 'sorftime') return '数派';
  return '卖家精灵';
}

function renderUsageRangeHint() {
  const hint = document.getElementById('cost-calc-usage-range-hint');
  if (hint) hint.textContent = getUsageRangeText();
}

function getUsageRangeText() {
  const startDate = usagePayload?.startDate || getCurrentMonthStart();
  const endDate = usagePayload?.endDate || getTodayString();
  return `数据范围: ${startDate} ~ ${endDate}`;
}

function getTodayString() {
  return formatBeijingDateKey();
}

function getCurrentMonthStart() {
  return formatBeijingMonthStart();
}

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '¥0.00';
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
